import { useQuery } from '@tanstack/react-query';
import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { organizationApi } from 'src/api/organization';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useUserContext } from 'src/auth/useUserContext';
import { setSelectedOrganization } from 'src/redux/slices/organization';
import { useDispatch, useSelector } from 'src/redux/store';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Roles } from 'src/types/User';
import { GetUserOrganizationsDto, OrganizationInUserDto } from 'woofwoof-api';
import {
  OrganizationStorageBoundary,
  organizationStorageIdentity,
} from './OrganizationStorageBoundary';
// ----------------------------------------------------------------------

type SelectedOrganization = GetUserOrganizationsDto & { role: Roles };

interface OrganizationContextType {
  organizations: GetUserOrganizationsDto[];
  selectedOrganization: SelectedOrganization | null;
  selectedRole: Roles | null;
  isLoading: boolean;
  isError: boolean;
  setSelectedOrganization: (
    organization: GetUserOrganizationsDto,
    role: Roles,
  ) => void;
  getUserAvailableRoles: (organizationId: string) => Roles[];
  refetch: () => Promise<any>;
  isOrganizationAdminWithoutOrganizations?: boolean;
  isProfessionalRoleSelected?: boolean;
  isOrganizationAdminRoleSelected?: boolean;
  isOrganizationNotVerified?: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

interface OrganizationProviderProps {
  children: React.ReactNode;
}

const hasOrganizationChanged = (
  selected: SelectedOrganization,
  fresh: GetUserOrganizationsDto,
): boolean =>
  (Object.keys(fresh) as Array<keyof GetUserOrganizationsDto>).some(
    (key) => selected[key] !== fresh[key],
  );

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    isAuthenticated,
    isInitialized,
    user: authenticatedUser,
  } = useAuthContext();
  const { selectedOrganization } = useSelector((state) => state.organization);
  const {
    data: userData,
    isLoading: userLoading,
    isOrganizationAdminRole,
    isOrganizationOwnerRole,
    isProfessionalRole,
  } = useUserContext();

  // Fetch organizations
  const {
    data: organizationsData,
    isLoading,
    isError,
    refetch: refetchOrganizations,
    remove,
  } = useQuery({
    queryKey: ['user-organizations', userData?.id],
    queryFn: () =>
      organizationApi.organizationControllerGetOrganizationsByUserId({
        userId: userData?.id || '',
      }),
    enabled:
      !!userData?.id &&
      (isOrganizationAdminRole ||
        isOrganizationOwnerRole ||
        isProfessionalRole) &&
      isAuthenticated,
    retry: 3,
    retryDelay: 2000,
  });

  const organizations = useMemo(
    () => organizationsData?.data || [],
    [organizationsData],
  );

  const isOrganizationAdminWithoutOrganizations = useMemo(
    () =>
      isOrganizationAdminRole && !isProfessionalRole && !organizations.length,
    [isOrganizationAdminRole, organizations, isProfessionalRole],
  );

  const getUserAvailableRoles = useCallback(
    (organizationId: string): Roles[] => {
      const userOrg = userData?.organizations.find(
        (org: OrganizationInUserDto) => org.organization === organizationId,
      );
      if (!userOrg?.roles) return [];

      // Convert API roles to our internal Roles enum
      const supportedRoles: Roles[] = [
        Roles.organization_admin,
        Roles.organization_owner,
        Roles.professional,
      ];

      return userOrg.roles
        .map((role) => {
          // Map API role enum to our internal enum
          switch (role) {
            case 'ORGANIZATION_ADMIN':
              return Roles.organization_admin;
            case 'ORGANIZATION_OWNER':
              return Roles.organization_owner;
            case 'PROFESSIONAL':
              return Roles.professional;
            default:
              return null;
          }
        })
        .filter(
          (role) => role !== null && supportedRoles.includes(role),
        ) as Roles[];
    },
    [userData],
  );

  const handleSetSelectedOrganization = useCallback(
    (organization: GetUserOrganizationsDto, role: Roles) => {
      dispatch(setSelectedOrganization({ organization, role }));
      if (role === Roles.organization_admin) {
        navigate(PATH_DASHBOARD.general.calendar);
      }
      if (role === Roles.professional) {
        navigate(PATH_DASHBOARD.general.waitingRoom);
      }
    },
    [dispatch, navigate],
  );

  const syncSelectedOrganization = useCallback(
    (freshOrganizations: GetUserOrganizationsDto[]) => {
      if (!selectedOrganization) return;
      const freshOrganization = freshOrganizations.find(
        (org) => org.id === selectedOrganization.id,
      );
      if (
        freshOrganization &&
        hasOrganizationChanged(selectedOrganization, freshOrganization)
      ) {
        dispatch(
          setSelectedOrganization({
            organization: freshOrganization,
            role: selectedOrganization.role,
          }),
        );
      }
    },
    [selectedOrganization, dispatch],
  );

  const refetch = useCallback(async () => {
    const result = await refetchOrganizations();
    if (result.data?.data) syncSelectedOrganization(result.data.data);
    return result;
  }, [refetchOrganizations, syncSelectedOrganization]);

  // The selection is persisted across sessions, so a snapshot taken before an
  // admin verified or renamed the organization must yield to fresh data.
  useEffect(() => {
    if (isAuthenticated) syncSelectedOrganization(organizations);
  }, [isAuthenticated, organizations, syncSelectedOrganization]);

  useEffect(() => {
    if (!selectedOrganization && isAuthenticated && organizations.length > 0) {
      const preferredOrg =
        organizations.find((org) => org.isVerified) ?? organizations[0];
      const availableRoles = getUserAvailableRoles(preferredOrg.id);
      if (availableRoles.length > 0) {
        // Prioritize ORGANIZATION_ADMIN role if available, otherwise use first role
        const role = availableRoles.includes(Roles.organization_admin)
          ? Roles.organization_admin
          : availableRoles[0];
        // Only set state; navigation is handled by AuthGuard/explicit user actions
        dispatch(setSelectedOrganization({ organization: preferredOrg, role }));
      }
    }
  }, [
    selectedOrganization,
    organizations,
    isAuthenticated,
    getUserAvailableRoles,
    dispatch,
  ]);

  useEffect(() => {
    if (
      isAuthenticated &&
      selectedOrganization &&
      organizations.length > 0 &&
      !organizations.find(
        (org: GetUserOrganizationsDto) => org.id === selectedOrganization.id,
      )
    ) {
      const preferredOrg =
        organizations.find((org) => org.isVerified) ?? organizations[0];
      const availableRoles = getUserAvailableRoles(preferredOrg.id);
      if (availableRoles.length > 0) {
        const role = availableRoles[0];
        // Only set state; navigation is handled by AuthGuard/explicit user actions
        dispatch(setSelectedOrganization({ organization: preferredOrg, role }));
      }
    }
  }, [
    selectedOrganization,
    organizations,
    getUserAvailableRoles,
    isAuthenticated,
    dispatch,
  ]);

  const needsOrganization = Boolean(
    isOrganizationAdminRole || isOrganizationOwnerRole || isProfessionalRole,
  );
  const currentOrganization = needsOrganization
    ? organizations.find(
        (organization) => organization.id === selectedOrganization?.id,
      )
    : undefined;
  const storagePending =
    isAuthenticated &&
    (!isInitialized ||
      userLoading ||
      !userData?.id ||
      !authenticatedUser?.uid ||
      (needsOrganization &&
        (isLoading || (organizations.length > 0 && !currentOrganization))));
  const storageIdentity =
    isAuthenticated && !storagePending && userData?.id
      ? organizationStorageIdentity({
          accountId: authenticatedUser!.uid,
          userId: userData.id,
          organizationId: currentOrganization?.id ?? null,
          role: currentOrganization ? selectedOrganization?.role ?? null : null,
        })
      : null;

  // Consumers see fresh data in the same render the query resolves, before the
  // sync effect above persists it, so AuthGuard never acts on a stale snapshot.
  const effectiveSelectedOrganization = useMemo(
    () =>
      selectedOrganization &&
      currentOrganization &&
      hasOrganizationChanged(selectedOrganization, currentOrganization)
        ? { ...currentOrganization, role: selectedOrganization.role }
        : selectedOrganization,
    [selectedOrganization, currentOrganization],
  );

  const contextValue = useMemo(
    () => ({
      organizations,
      selectedOrganization: effectiveSelectedOrganization,
      selectedRole: selectedOrganization?.role || null,
      isLoading: isLoading || userLoading,
      isError,
      setSelectedOrganization: handleSetSelectedOrganization,
      getUserAvailableRoles,
      refetch,
      isOrganizationAdminWithoutOrganizations,
      isProfessionalRoleSelected:
        selectedOrganization?.role === Roles.professional && isProfessionalRole,
      isOrganizationAdminRoleSelected:
        selectedOrganization?.role === Roles.organization_admin &&
        isOrganizationAdminRole,
      isOrganizationNotVerified:
        !!effectiveSelectedOrganization &&
        effectiveSelectedOrganization.isVerified === false,
    }),
    [
      organizations,
      selectedOrganization,
      effectiveSelectedOrganization,
      isLoading,
      userLoading,
      isError,
      handleSetSelectedOrganization,
      getUserAvailableRoles,
      refetch,
      isOrganizationAdminWithoutOrganizations,
      isProfessionalRole,
      isOrganizationOwnerRole,
      isOrganizationAdminRole,
    ],
  );

  return (
    <OrganizationContext.Provider value={contextValue}>
      <OrganizationStorageBoundary
        identity={storageIdentity}
        pending={storagePending}
      >
        {children}
      </OrganizationStorageBoundary>
    </OrganizationContext.Provider>
  );
}

export { OrganizationContext };
export type { OrganizationContextType };
